"use client";

import { Armchair, BookOpen, Coffee, Flower2, Leaf, Sparkles } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function EditorialIndependencePromise() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const grid = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const quote = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const comforts = [{
    icon: Armchair,
    title: "Unbiased Analysis",
    body: "Our reviews are built on rigorous testing protocols. We never accept payment for positive coverage, ensuring your security choices remain truly yours.",
    align: "items-start"
  }, {
    icon: Leaf,
    title: "Transparent Data",
    body: "We document every speed test and privacy audit with full clarity. Our methodology is open, verifiable, and focused on your digital safety.",
    align: "items-end"
  }, {
    icon: Flower2,
    title: "User-First Focus",
    body: "Your privacy is our only priority. We evaluate VPNs based on real-world performance, not marketing claims or affiliate incentives.",
    align: "items-center"
  }, {
    icon: BookOpen,
    title: "Expert Research",
    body: "Our team of cybersecurity analysts continuously monitors the threat landscape to provide you with the most accurate, up-to-date protection advice.",
    align: "items-end"
  }, {
    icon: Sparkles,
    title: "Verified Results",
    body: "Every benchmark is conducted in controlled environments. We verify encryption standards and leak protection so you can browse with confidence.",
    align: "items-start"
  }, {
    icon: Coffee,
    title: "Honest Guidance",
    body: "We simplify complex security topics into actionable insights. No jargon, no hidden agendas—just clear, honest advice for your digital life.",
    align: "items-center"
  }];
  return <section data-section-id="4984" id="editorialindependencepromise" className="relative py-24 md:py-36 lg:py-[180px] px-6 md:px-12 lg:px-20 bg-background text-foreground">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={header.ref} className={`max-w-3xl mb-20 md:mb-28 transition-all duration-1000 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs md:text-sm tracking-[0.25em] uppercase text-muted-foreground mb-6">
            III &mdash; Our Commitment
          </p>
          <h2 className="font-default text-4xl md:text-5xl lg:text-[48px] leading-[1.1] text-foreground tracking-tight">
            The standards{" "}
            <span className="block md:inline">
              that <em className="italic font-normal">define</em> our work.
            </span>
          </h2>
        </div>

        {}
        <div ref={grid.ref} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 relative">
          {comforts.map((comfort, i) => {
          const Icon = comfort.icon;
          const isLastCol = (i + 1) % 3 === 0;
          const isLastColMd = (i + 1) % 2 === 0;
          return <div key={i} data-index={i} className={`relative flex flex-col ${comfort.align} px-0 md:px-8 lg:px-10 py-12 md:py-14 transition-all duration-1000 ease-out ${grid.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            transitionDelay: `${i * 120}ms`
          }}>
                {}
                <span aria-hidden="true" className={`hidden md:block absolute top-8 bottom-8 right-0 w-px bg-border ${isLastCol ? "lg:hidden" : "lg:block"} ${isLastColMd ? "md:hidden lg:block" : ""}`} />

                <div className="max-w-sm">
                  <Icon className="text-primary mb-8" style={{
                width: 56,
                height: 56,
                strokeWidth: 1.25
              }} aria-hidden="true" />
                  <h3 className="font-default text-2xl md:text-[24px] leading-snug text-foreground mb-4 tracking-tight">
                    {comfort.title}
                  </h3>
                  <p className="font-default text-base leading-relaxed text-muted-foreground">
                    {comfort.body}
                  </p>
                </div>
              </div>;
        })}
        </div>

        {}
        <div ref={quote.ref} className={`mt-20 md:mt-28 lg:mt-32 flex items-center justify-center transition-all duration-1000 ease-out ${quote.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex items-center justify-center gap-6 md:gap-10 max-w-4xl text-center px-4">
            <span aria-hidden="true" className="font-default text-2xl md:text-3xl text-muted-foreground/40 select-none">
              *
            </span>
            <p className="font-default italic text-2xl md:text-[28px] leading-snug text-foreground tracking-tight">
              Integrity is our foundation. Trust is our goal.
            </p>
            <span aria-hidden="true" className="font-default text-2xl md:text-3xl text-muted-foreground/40 select-none">
              *
            </span>
          </div>
        </div>
      </div>
    </section>;
}